import React, { useEffect, useRef, useState } from 'react';

// Combobox de grupos de producto: filtra `grupos` mientras se escribe y
// permite seleccionar uno existente (onSeleccionar) o crear uno nuevo con
// el texto tipeado si no coincide con ninguno (onCrear).
//
// Teclado (sin mouse): ↓/↑ recorren las opciones, Enter confirma la
// resaltada (guardar), Esc cierra el desplegable (cancelar).
export default function AutocompleteGrupo({ grupos, onSeleccionar, onCrear, placeholder, autoFocus = true }) {
  const [texto, setTexto] = useState('');
  const [abierto, setAbierto] = useState(false);
  const [resaltado, setResaltado] = useState(0);
  const contenedorRef = useRef(null);
  const inputRef = useRef(null);
  const listaRef = useRef(null);

  useEffect(() => {
    function alClickearFuera(e) {
      if (contenedorRef.current && !contenedorRef.current.contains(e.target)) setAbierto(false);
    }
    document.addEventListener('mousedown', alClickearFuera);
    return () => document.removeEventListener('mousedown', alClickearFuera);
  }, []);

  const q = texto.trim().toLowerCase();
  const filtrados = q ? grupos.filter((g) => g.nombre.toLowerCase().includes(q)).slice(0, 8) : grupos.slice(0, 8);
  const coincideExacto = grupos.some((g) => g.nombre.toLowerCase() === q);
  const mostrarCrear = q.length > 0 && !coincideExacto;

  // Opciones navegables, en el mismo orden en que se pintan.
  const opciones = [
    ...filtrados.map((g) => ({ tipo: 'sel', grupo: g })),
    ...(mostrarCrear ? [{ tipo: 'crear' }] : [])
  ];

  // Al cambiar lo escrito, volver a resaltar la primera opción.
  useEffect(() => { setResaltado(0); }, [texto]);

  // Mantener visible la opción resaltada.
  useEffect(() => {
    if (!abierto || !listaRef.current) return;
    const el = listaRef.current.children[resaltado];
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [resaltado, abierto]);

  function activar(opcion) {
    if (!opcion) return;
    setTexto('');
    setAbierto(false);
    if (opcion.tipo === 'crear') onCrear(texto.trim());
    else onSeleccionar(opcion.grupo);
  }

  function alTeclear(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setAbierto(true);
      setResaltado((i) => Math.min(i + 1, opciones.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setResaltado((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (abierto && opciones.length > 0) {
        e.preventDefault();
        activar(opciones[resaltado] || opciones[0]);
      }
    } else if (e.key === 'Escape') {
      if (abierto) {
        e.preventDefault();
        e.stopPropagation();
        setAbierto(false);
      } else {
        inputRef.current?.blur();
      }
    }
  }

  return (
    <div className="relative" ref={contenedorRef}>
      <input
        ref={inputRef}
        autoFocus={autoFocus}
        value={texto}
        onChange={(e) => { setTexto(e.target.value); setAbierto(true); }}
        onFocus={() => setAbierto(true)}
        onKeyDown={alTeclear}
        placeholder={placeholder || 'Buscar o crear grupo...'}
        className="border rounded-md px-2 py-1.5 w-full text-sm"
        autoComplete="off"
      />
      {abierto && opciones.length > 0 && (
        <ul ref={listaRef} className="absolute z-10 mt-1 w-full bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto text-sm">
          {filtrados.map((g, idx) => (
            <li
              key={g.id}
              onClick={() => activar({ tipo: 'sel', grupo: g })}
              onMouseEnter={() => setResaltado(idx)}
              className={`px-3 py-1.5 cursor-pointer ${resaltado === idx ? 'bg-primary-50' : ''}`}
            >
              {g.nombre}
            </li>
          ))}
          {mostrarCrear && (
            <li
              onClick={() => activar({ tipo: 'crear' })}
              onMouseEnter={() => setResaltado(filtrados.length)}
              className={`px-3 py-1.5 cursor-pointer italic text-primary-700 ${filtrados.length > 0 ? 'border-t' : ''} ${resaltado === filtrados.length ? 'bg-primary-50' : ''}`}
            >
              Crear grupo "{texto.trim()}"
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
